import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminCinemas,
  adminCinemaSectors,
  adminCreateSession,
  adminSessions,
  adminUpdateSession,
  adminUploadPhoto,
} from '../../api/endpoints';
import TopBar from '../../components/TopBar';
import { showAlert } from '../../lib/tg';
import { mediaUrl } from '../../lib/media';

export default function SessionForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = !!id;
  const editId = isEdit ? Number(id) : null;

  const { data: cinemas } = useQuery({ queryKey: ['admin-cinemas'], queryFn: adminCinemas });
  // When editing, we pull the existing session from the list endpoint to
  // prefill — avoids adding another route just for one fetch.
  const { data: allSessions } = useQuery({
    queryKey: ['admin-sessions', false],
    queryFn: () => adminSessions(false),
    enabled: isEdit,
  });
  // Also check archive (a session being edited could be from the past).
  const { data: archiveSessions } = useQuery({
    queryKey: ['admin-sessions', true],
    queryFn: () => adminSessions(true),
    enabled: isEdit,
  });
  const existing = useMemo(() => {
    if (!isEdit) return null;
    return (
      (allSessions || []).find((s: any) => s.id === editId) ||
      (archiveSessions || []).find((s: any) => s.id === editId) ||
      null
    );
  }, [isEdit, allSessions, archiveSessions, editId]);

  const [cinemaId, setCinemaId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [duration, setDuration] = useState(120);
  const [posterFileId, setPosterFileId] = useState<string | null>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [posterUploading, setPosterUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [prefilled, setPrefilled] = useState(false);

  // Prefill from existing session once.
  useEffect(() => {
    if (!isEdit || !existing || prefilled) return;
    setCinemaId(existing.cinema_id);
    setTitle(existing.title || '');
    setDescription(existing.description || '');
    // Convert ISO -> 'YYYY-MM-DDTHH:mm' for datetime-local (in local tz).
    if (existing.starts_at) {
      const d = new Date(existing.starts_at);
      const pad = (n: number) => String(n).padStart(2, '0');
      const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      setStartsAt(local);
    }
    setDuration(existing.duration_min || 120);
    setPosterFileId(existing.poster_file_id || null);
    if (existing.poster_url) {
      setPosterPreview(mediaUrl(existing.poster_url) || null);
    }
    if (existing.prices) setPrices({ ...existing.prices });
    setPrefilled(true);
  }, [isEdit, existing, prefilled]);

  const { data: sectors } = useQuery({
    queryKey: ['cinema-sectors', cinemaId],
    queryFn: () => adminCinemaSectors(cinemaId!),
    enabled: !!cinemaId,
  });

  const uniqueSectorNames = useMemo(() => {
    if (!sectors) return [];
    return Array.from(new Set(sectors.map((s) => s.name)));
  }, [sectors]);

  useEffect(() => {
    setPrices((p) => {
      const next: Record<string, number> = {};
      uniqueSectorNames.forEach((n) => (next[n] = p[n] ?? 0));
      return next;
    });
  }, [uniqueSectorNames]);

  const mut = useMutation({
    mutationFn: () => {
      const payload = {
        cinema_id: cinemaId,
        title,
        description,
        starts_at: new Date(startsAt).toISOString(),
        duration_min: Math.max(1, duration || 0),
        poster_file_id: posterFileId,
        prices: Object.fromEntries(
          uniqueSectorNames.map((n) => [n, Math.max(0, prices[n] || 0)]),
        ),
        status: existing?.status || 'published',
      };
      return isEdit && editId
        ? adminUpdateSession(editId, payload)
        : adminCreateSession(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-sessions'] });
      qc.invalidateQueries({ queryKey: ['session', String(editId)] });
      navigate('/admin/sessions', { replace: true });
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
          : 'Ошибка сохранения';
      showAlert(msg);
    },
  });

  const valid =
    cinemaId &&
    title &&
    startsAt &&
    (duration || 0) > 0 &&
    uniqueSectorNames.length > 0 &&
    uniqueSectorNames.every((n) => (prices[n] || 0) > 0);

  return (
    <div className="min-h-screen pb-32">
      <TopBar title={isEdit ? 'Редактировать сеанс' : 'Новый сеанс'} />
      <div className="px-4 pt-3 space-y-3">
        <div>
          <label className="label">Кинотеатр</label>
          <select
            className="input disabled:opacity-60"
            value={cinemaId ?? ''}
            disabled={isEdit}
            onChange={(e) => setCinemaId(Number(e.target.value))}
          >
            <option value="" disabled>Выберите…</option>
            {cinemas?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {isEdit && (
            <div className="text-xs text-muted mt-1">
              Кинотеатр нельзя менять — пересоздайте сеанс, если нужно.
            </div>
          )}
        </div>
        <div>
          <label className="label">Название</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label className="label">Дата и время</label>
          <input
            className="input disabled:opacity-60"
            type="datetime-local"
            value={startsAt}
            disabled={isEdit}
            onChange={(e) => setStartsAt(e.target.value)}
          />
          {isEdit && (
            <div className="text-xs text-muted mt-1">
              Дату/время нельзя менять у существующего сеанса.
            </div>
          )}
        </div>
        <div>
          <label className="label">Длительность (мин)</label>
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={1}
            value={duration === 0 ? '' : duration}
            onChange={(e) => {
              const v = e.target.value;
              setDuration(v === '' ? 0 : Number(v));
            }}
            onBlur={(e) => {
              const n = Number(e.target.value);
              setDuration(Math.max(1, isNaN(n) ? 1 : n));
            }}
          />
        </div>
        <div>
          <label className="label">Постер</label>
          <div className="text-xs text-muted mb-2">
            Рекомендуется 3:4 (вертикальный), ≥ 900×1200 px, JPG/PNG/WebP до 10 МБ.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setPosterUploading(true);
              const localPreview = URL.createObjectURL(f);
              setPosterPreview(localPreview);
              try {
                const res = await adminUploadPhoto(f);
                setPosterFileId(res.file_id);
              } catch {
                showAlert('Ошибка загрузки фото');
                setPosterPreview(null);
              } finally {
                setPosterUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }
            }}
          />
          {posterPreview ? (
            <div className="flex items-center gap-3">
              <img src={posterPreview} alt="poster" className="w-20 h-28 object-cover rounded-lg2 border border-line" />
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={posterUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {posterUploading ? 'Загрузка…' : 'Заменить'}
                </button>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => {
                    setPosterFileId(null);
                    setPosterPreview(null);
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary w-full"
              disabled={posterUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {posterUploading ? 'Загрузка…' : 'Выбрать из галереи'}
            </button>
          )}
        </div>
        <div>
          <label className="label">Описание</label>
          <textarea className="input min-h-24" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        {uniqueSectorNames.length > 0 && (
          <div className="card p-3">
            <div className="font-semibold mb-2">Цены по секторам (UZS)</div>
            {uniqueSectorNames.map((n) => (
              <div key={n} className="flex items-center gap-2 mb-2">
                <div className="flex-1 text-sm">{n}</div>
                <input
                  className="input max-w-[140px]"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={!prices[n] ? '' : prices[n]}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrices({ ...prices, [n]: v === '' ? 0 : Number(v) });
                  }}
                  onBlur={(e) => {
                    const num = Number(e.target.value);
                    setPrices({ ...prices, [n]: Math.max(0, isNaN(num) ? 0 : num) });
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line">
        <button className="btn-primary" disabled={!valid || mut.isPending || posterUploading} onClick={() => mut.mutate()}>
          {isEdit ? 'Сохранить' : 'Опубликовать'}
        </button>
      </div>
    </div>
  );
}
